import { CSSProperties, FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@tremor/react';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import { OverviewMetrics } from '../../services/service-evaluations';
import { EvaluationRecipientStatus, EvaluationRequestStatus } from '../../util/evaluationContract';
import {
  StatusMeta,
  recipientStatusMeta,
  requestStatusMeta,
} from '../../util/evaluationStatusMeta';
import styles from './app-evaluation-overview.module.scss';

type Props = {
  /** null while loading or when the whole request failed. */
  metrics: OverviewMetrics | null;
};

type Tile = {
  key: string;
  title: string;
  subtitle: string | null;
  value: number | null;
  tone: StatusMeta;
  icon: ReactNode;
  /** Only a late count asks for attention; the other numbers stay neutral. */
  alert?: boolean;
};

const ICON_SIZE = { fontSize: 16 };
const GREY_TONE = requestStatusMeta(EvaluationRequestStatus.Closed);

const toneStyle = (tile: Tile): CSSProperties => {
  const meta = tile.value ? tile.tone : GREY_TONE;
  return { '--tone-bg': meta.backgroundColor, '--tone-text': meta.color } as CSSProperties;
};

const monthName = (month: string, locale: string) =>
  new Intl.DateTimeFormat(locale, { timeZone: 'UTC', month: 'long' }).format(
    new Date(`${month}-01T00:00:00.000Z`),
  );

const OverviewTiles: FC<Props> = ({ metrics }) => {
  const [t, i18n] = useTranslation('evaluations');

  const tiles: Tile[] = [
    {
      key: 'ongoing',
      title: t('overview.tiles.ongoing.title', 'Pågående utvärderingar'),
      subtitle: t('overview.tiles.ongoing.subtitle', 'som du har behörighet att se'),
      value: metrics?.ongoing ?? null,
      tone: requestStatusMeta(EvaluationRequestStatus.Active),
      icon: <AssignmentOutlinedIcon style={ICON_SIZE} />,
    },
    {
      key: 'waiting',
      title: t('overview.tiles.waiting.title', 'Väntande svar'),
      subtitle:
        metrics?.waitingRequests === null || metrics?.waitingRequests === undefined
          ? null
          : t('overview.tiles.waiting.subtitle', {
              count: metrics.waitingRequests,
              defaultValue_one: 'i {{count}} utvärdering',
              defaultValue_other: 'i {{count}} utvärderingar',
            }),
      value: metrics?.waiting ?? null,
      tone: recipientStatusMeta(EvaluationRecipientStatus.Sent),
      icon: <ScheduleIcon style={ICON_SIZE} />,
    },
    {
      key: 'overdue',
      title: t('overview.tiles.overdue.title', 'Försenade'),
      subtitle: t('overview.tiles.overdue.subtitle', 'behöver påminnelse'),
      value: metrics?.overdue ?? null,
      tone: requestStatusMeta(EvaluationRequestStatus.Overdue),
      icon: <ErrorOutlineIcon style={ICON_SIZE} />,
      alert: true,
    },
    {
      key: 'completed',
      title: t('overview.tiles.completed.title', 'Svar denna månad'),
      subtitle: metrics
        ? t('overview.tiles.completed.subtitle', 'inkomna i {{month}}', {
            month: monthName(metrics.month, i18n.language),
          })
        : null,
      value: metrics?.completedThisMonth ?? null,
      tone: requestStatusMeta(EvaluationRequestStatus.Completed),
      icon: <CheckCircleOutlinedIcon style={ICON_SIZE} />,
    },
  ];

  return (
    <div className={styles.metricsGrid}>
      {tiles.map(tile => (
        <Card key={tile.key} className={styles.metricCard}>
          <div className={styles.metricCardText} data-tile={tile.key}>
            <p className={styles.metricCardTitle}>{tile.title}</p>
            <p
              className={`${styles.metricCardNumber} ${
                tile.alert && tile.value ? styles.metricCardNumberAlert : ''
              }`}
              data-tile-count=""
            >
              {tile.value === null ? '–' : tile.value}
            </p>
            {tile.subtitle && <p className={styles.metricCardSubtitle}>{tile.subtitle}</p>}
          </div>
          <div className={styles.metricCardIcon} style={toneStyle(tile)}>
            {tile.icon}
          </div>
        </Card>
      ))}
    </div>
  );
};

export default OverviewTiles;
